import { useAuth } from "@/core/providers/auth.provider";
import { AdminSettingsDialog } from "@/dialogs/admin-settings-dialog";
import { FeatureFlagsDialog } from "@/dialogs/feature-flags-dialog";
import { LlmPromptsEditorDialog } from "@/dialogs/llm-prompts-editor-dialog";
import NotionDeployment from "@/dialogs/notion-deployment";
import { track } from "@/features/analytics";
import { useGetCurrentUserQuery } from "@/shared/services";
import { HelpNavMenu } from "@components/help-nav-menu";
import { CreditsPill } from "@features/platform-onboarding/components/credits-pill";
import { CREDITS_ENABLED } from "@features/platform-onboarding/constants";
import { UserMenuDeleteAccountDialog } from "@components/sidebar/components/user-menu/components/dialogs";
import Icon from "@components/ui/icon";
import Popover from "@components/ui/popover";
import { useAccountStoreShallow } from "@features/account-management";
import { useAuthStore } from "@features/authentication";
import { cn } from "@lib/cn";
import { useAppDispatch } from "@store";
import { openDialog } from "@store/dialogs";
import {
  IconBrandNotion,
  IconCookie,
  IconFlag,
  IconGift,
  IconId,
  IconLogout,
  IconSelector,
  IconSettingsStar,
  IconSparkles,
  IconThumbUp,
  IconTrash,
  IconUserCircle,
} from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { downloadAgentFeedbackCsv } from "@editor/modules/header/ai/feedback/download-feedback";
import { useNavigate } from "react-router-dom";
import { UserMenuAdminSettings } from "./components/user-menu-admin-settings";

declare const window: Window & {
  Cookiebot?: {
    renew(): void;
  };
};

const getInitials = (firstName: string | undefined) =>
  (firstName ?? "")
    .split(" ")
    .slice(0, 2)
    .map((part) => part[0] ?? "")
    .join("")
    .toUpperCase();

const items: MenuItem[] = [
  {
    id: "my-settings",
    label: "My settings",
    Icon: IconId,
    action: "my-settings",
  },
  {
    id: "manage-cookies",
    label: "Manage cookies",
    Icon: IconCookie,
    action: "manage-cookies",
  },
  {
    id: "become-an-affiliate",
    label: "Become an affiliate",
    Icon: IconGift,
    action: "become-an-affiliate",
  },
  {
    id: "admin-settings",
    label: "Admin settings",
    Icon: IconSettingsStar,
    action: "admin-settings",
    internal: true,
  },
  {
    id: "notion-deployment",
    label: "Notion deployment",
    Icon: IconBrandNotion,
    action: "notion-deployment",
    internal: true,
  },
  {
    id: "llm-prompts",
    label: "Edit LLM prompts",
    Icon: IconSparkles,
    action: "llm-prompts",
    internal: true,
  },
  {
    id: "agent-feedback",
    label: "Agent feedback (CSV)",
    Icon: IconThumbUp,
    action: "agent-feedback",
    internal: true,
  },
  {
    id: "feature-flags",
    label: "Feature Flags",
    Icon: IconFlag,
    action: "feature-flags",
    internal: true,
  },
  {
    id: "delete-account",
    label: "Delete account",
    Icon: IconTrash,
    action: "delete-account",
    internal: true,
  },
  {
    id: "logout",
    label: "Log out",
    Icon: IconLogout,
    destructive: true,
    action: "logout",
  },
];

type MenuItem = {
  id: string;
  label: string;
  Icon: typeof IconUserCircle;
  destructive?: boolean;
  action: string;
  internal?: boolean;
};

const FEATUER_FLAGS_ALLOW_LIST = [
  "vladz@storydoc.com",
  "dawidd@storydoc.com",
  "yaacovg@storydoc.com",
  "amoza@storydoc.com",
  "beatrizo@storydoc.com",
  "diogos@storydoc.com",
];

export const UserNavMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { data: user } = useGetCurrentUserQuery();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isAdmin = useAuthStore((s) => s.isAdmin);
  const isAdminCore = useAuthStore((s) => s.isAdminCore);

  const handleClose = () => setIsOpen(false);

  const handleOpenChange = (next: boolean) => {
    setIsOpen(next);
    if (next) track("open user nav menu");
  };

  const menuItems = useMemo(() => {
    return items.filter((item) => {
      if (item.id === "feature-flags" && item.internal && isAdmin) {
        return FEATUER_FLAGS_ALLOW_LIST.includes(user?.email ?? "");
      }
      return !item.internal || isAdmin;
    });
  }, [isAdmin]);

  const handleAction = async (item: MenuItem) => {
    track("click user nav menu item", { item: item.action.replace(/-/g, " ") });
    switch (item.action) {
      case "my-settings":
        navigate("/pages/settings");
        break;
      case "manage-cookies":
        window.Cookiebot?.renew();
        break;
      case "become-an-affiliate":
        window.open("https://storydoc.getrewardful.com/signup", "_blank");
        break;
      case "logout":
        await logout();
        navigate("/login", { replace: true });
        break;
      case "admin-settings":
        dispatch(
          openDialog({
            id: "uZknTZTeNMV3lZSLhBSKR",
            content: AdminSettingsDialog,
          }),
        );
        break;
      case "notion-deployment":
        dispatch(
          openDialog({
            id: NotionDeployment.id,
            content: NotionDeployment,
          }),
        );
        break;
      case "llm-prompts":
        dispatch(
          openDialog({
            id: LlmPromptsEditorDialog.dialogId,
            content: LlmPromptsEditorDialog,
          }),
        );
        break;
      case "agent-feedback":
        // Every thumbs up/down on an agent reply, with the request and the
        // reply (ai/feedback/download-feedback.ts).
        void downloadAgentFeedbackCsv().then((saved) => {
          if (!saved) toast.error("Couldn't download the agent feedback.");
        });
        break;
      case "feature-flags":
        dispatch(
          openDialog({
            id: FeatureFlagsDialog.dialogId,
            content: FeatureFlagsDialog,
          }),
        );
        break;
      case "delete-account":
        dispatch(
          openDialog({
            id: UserMenuDeleteAccountDialog.dialogId,
            content: UserMenuDeleteAccountDialog,
          }),
        );
        break;
      default:
        break;
    }
    handleClose();
  };

  const initials = getInitials(user?.firstName);
  const displayName = (user?.firstName ?? "").split(" ")[0] || "";

  return (
    <div className="tw-flex tw-items-center tw-gap-0.5">
      {CREDITS_ENABLED && <CreditsPill />}
      <Popover
        placement="bottom-end"
        open={isOpen}
        onOpenChange={handleOpenChange}
      >
        <Popover.Trigger
          className={cn(
            "tw-flex tw-cursor-pointer tw-items-center tw-rounded-full tw-p-0.5 tw-transition-colors hover:tw-bg-blue-50",
            isOpen && "tw-bg-blue-50",
          )}
        >
          <div className="tw-relative tw-mr-2">
            <div className="tw-flex tw-size-8 tw-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-full tw-border tw-border-solid tw-border-blue/10 tw-bg-[#F3F2F5] tw-font-sans tw-text-xs tw-font-medium tw-uppercase tw-text-blue">
              {user?.profile?.picture ? (
                <img
                  width={32}
                  height={32}
                  src={user.profile.picture}
                  alt={displayName}
                  className="tw-block tw-size-full tw-object-cover"
                />
              ) : (
                initials
              )}
            </div>
            <AccountPlanLabel />
          </div>
          {displayName && (
            <p className="tw-m-0 tw-mr-0.5 tw-truncate tw-whitespace-nowrap tw-font-sans tw-text-sm tw-font-medium tw-leading-5 tw-text-blue">
              {displayName}
            </p>
          )}
          <span className="tw-flex tw-size-6 tw-items-center tw-justify-center">
            <IconSelector
              size={14}
              stroke={1.75}
              className="tw-text-blue-500"
            />
          </span>
        </Popover.Trigger>
        <Popover.Content
          className={cn("tw-z-[99999] tw-w-[200px]", isAdminCore && "tw-w-60")}
        >
          <div className="tw-flex tw-flex-col tw-rounded-lg tw-border tw-border-solid tw-border-blue/5 tw-bg-white tw-p-1 tw-shadow-sm">
            <div className="tw-flex tw-flex-col tw-gap-0.5">
              {menuItems.map((item) => {
                const { Icon, destructive } = item;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleAction(item)}
                    className={cn(
                      "tw-bg-transparent tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-md tw-border-none tw-px-2 tw-py-1.5 tw-text-left tw-text-sm tw-leading-5 tw-text-blue tw-transition-colors hover:tw-bg-blue-50",
                      destructive && "tw-text-[#dc2626] hover:tw-bg-red-50",
                    )}
                  >
                    <Icon
                      size={16}
                      stroke={1.5}
                      className={
                        destructive ? "tw-text-[#dc2626]" : "tw-text-blue"
                      }
                    />
                    {item.label}
                  </button>
                );
              })}
            </div>
            {isAdminCore && <UserMenuAdminSettings />}
          </div>
        </Popover.Content>
      </Popover>
      <HelpNavMenu />
    </div>
  );
};

const AccountPlanLabel = () => {
  const plan = useAccountStoreShallow((s) => s.planType);
  if (plan === "trial") return null;
  return (
    <div
      className={cn(
        "tw-absolute -tw-bottom-0.5 -tw-right-0.5 tw-flex tw-size-4 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid",
        plan === "team" && "tw-border-team-border tw-bg-team-base",
        plan === "pro" && "tw-border-pro-border tw-bg-pro-base",
        plan === "starter" && "tw-border-starter-border tw-bg-starter-base",
      )}
    >
      <Icon
        icon="ri-vip-crown-fill"
        className="tw-text-[9px]/none tw-text-white"
      />
    </div>
  );
};
